// Sugar Alarm — Cycle 457: "[Name]'s picture".
//
// Opened from a hero's row in the Heroes section. Anyone allowed to change
// the picture (the hero's own linked account, or the household admin — the
// server's `can_change_picture`, and the server decides again on save) gets:
// Take a photo · Upload · OR PICK ONE (the six presets) · Remove photo (only
// when a photo exists) · Save. Everyone else gets the same screen read-only.
//
// ⚠ CYCLE 458: ON THE KIOSK APK THERE IS NO "Upload" — not disabled, NOT
// RENDERED, and its gallery input is not mounted (`lib/photoSource.ts`, the
// APK signal, never the layout `variant`). "Take a photo" becomes the one
// full-width button under the preview. Phones and web are unchanged. The
// cycle-457.1 close-on-Save is keyed on the kiosk LAYOUT exactly as shipped.
//
// ⚠ IN FLOW, NEVER OVER ANYTHING. It renders IN PLACE of the hero list,
// inside the Settings pane — no portal, no overlay, no z-index, no `vh`/`vw`
// (`nothingCoversAlarm.test.ts`) — so it can never cover an alarm control;
// and sustained alarm audio pins the app to /now anyway (cycle 451.1), which
// unmounts it. The image cropper it opens is the existing AvatarCropper,
// already mounted below the alarm layer through `overlayHost`.
//
// ⚠ THE PHOTO BUTTONS ARE DISABLED WHILE ALARM AUDIO SOUNDS, EXACTLY AS
// CYCLE 455 DISABLES EVERY KIOSK PHOTO BUTTON (`usePhotoBlockedByAlarm`, the
// same line beneath them). Preset picks stay allowed: they open no camera.
//
// Nothing is written until Save: a pick, a photo or a removal is a DRAFT;
// Save sends exactly one request (PUT preset, POST photo or DELETE photo).
// The server re-crops, re-encodes and strips every photo whatever the
// cropper produced; the cropper exists so the person can frame the face.

import { useEffect, useMemo, useRef, useState } from "react";

import * as heroAvatarApi from "../../api/heroAvatar";
import { photoUploadErrorLine, HEIC_NOT_SUPPORTED_LINE } from "../../api/heroAvatar";
import { AvatarCropper } from "../AvatarCropper";
import { HeroAvatar } from "../ui/HeroAvatar";
import { toCroppableBlob, isHeicFile } from "../../lib/croppableImage";
import {
  HERO_AVATAR_ALT,
  HERO_AVATAR_PRESETS,
  heroAvatarSrc,
  HERO_AVATAR_TINT,
  type HeroAvatarPreset,
} from "../../lib/heroAvatars";
import { notifyHeroRosterChanged } from "../../lib/heroRosterSignal";
import { cameraOnlyDevice } from "../../lib/photoSource";
import { ACCEPT_IMAGE_CAMERA, ACCEPT_IMAGE_GALLERY } from "../../lib/imageAccept";
import {
  openPhotoInput,
  PHOTO_BLOCKED_LINE,
  usePhotoBlockedByAlarm,
} from "../../lib/nativeCamera";
import type { EmergencyContact, HeroAvatarPhoto } from "../../lib/types";
import styles from "./HeroPictureChooser.module.css";

export type ChooserVariant = "kiosk" | "mobile" | "web";

export type PictureDraft =
  | { kind: "unchanged" }
  | { kind: "preset"; preset: HeroAvatarPreset }
  | { kind: "photo"; blob: Blob; url: string }
  | { kind: "remove" };

/** What Save would send for a draft — pure, so the rule is testable. */
export function saveActionFor(
  draft: PictureDraft,
  current: { preset: string | null | undefined; photo: HeroAvatarPhoto | null | undefined },
): "none" | "preset" | "photo" | "remove" {
  switch (draft.kind) {
    case "unchanged":
      return "none";
    case "photo":
      return "photo";
    case "remove":
      return current.photo ? "remove" : "none";
    case "preset":
      // Re-picking the preset already showing, with no photo, is no change.
      return draft.preset === current.preset && !current.photo ? "none" : "preset";
  }
}

/** Which preset circle carries the selection ring. */
export function selectedPresetFor(
  draft: PictureDraft,
  current: { preset: string | null | undefined; photo: HeroAvatarPhoto | null | undefined },
): string | null {
  if (draft.kind === "preset") return draft.preset;
  if (draft.kind === "photo") return null;
  if (draft.kind === "remove") return current.preset ?? null;
  return current.photo ? null : current.preset ?? null;
}

interface Props {
  hero: EmergencyContact;
  variant: ChooserVariant;
  onClose: () => void;
  /**
   * The chooser is done; the caller collapses it. On the kiosk this fires on
   * the Save tap with the request still in flight (resolving to `null` on
   * success or to the one plain line to show when refused). On phone and web
   * it fires only after a successful save, with an already-resolved `null`.
   */
  onSubmitted: (pending: Promise<string | null>) => void;
  /** Injectable for tests. */
  api?: typeof heroAvatarApi;
  /** Cycle 458 — the kiosk APK is camera-only. Defaults to the one shared
   *  rule; injectable for tests. */
  cameraOnly?: boolean;
}

export function HeroPictureChooser({
  hero,
  variant,
  onClose,
  onSubmitted,
  api = heroAvatarApi,
  cameraOnly = cameraOnlyDevice(),
}: Props) {
  const name = hero.label;
  const canWrite = hero.can_change_picture === true;
  const photoBlocked = usePhotoBlockedByAlarm();
  const current = { preset: hero.avatar_preset ?? null, photo: hero.avatar_photo ?? null };

  const [draft, setDraft] = useState<PictureDraft>({ kind: "unchanged" });
  const [cropSource, setCropSource] = useState<Blob | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLElement | null>(null);
  const cameraRef = useRef<HTMLInputElement | null>(null);
  const galleryRef = useRef<HTMLInputElement | null>(null);

  // Opening the chooser brings its header to the top of the Settings pane,
  // so the picture and its controls start in view (the kiosk's tab bar and
  // the card header would otherwise push them below the fold).
  useEffect(() => {
    try {
      rootRef.current?.scrollIntoView?.({ block: "start" });
    } catch {
      /* an old WebView without options — the pane simply stays put */
    }
  }, []);

  // A draft photo's object URL is revoked when it is replaced or we leave.
  useEffect(() => {
    if (draft.kind !== "photo") return;
    const url = draft.url;
    return () => URL.revokeObjectURL(url);
  }, [draft]);

  const action = saveActionFor(draft, current);
  const selected = selectedPresetFor(draft, current);
  const hasPhoto =
    draft.kind === "photo" || (draft.kind !== "remove" && draft.kind !== "preset" && !!current.photo);

  const kiosk = variant === "kiosk";
  const previewSize = kiosk ? 200 : 160;
  const tileSize = kiosk ? 120 : 100;

  const onFile = async (f: File | undefined) => {
    if (!f) return;
    setError(null);
    try {
      setCropSource(await toCroppableBlob(f));
    } catch {
      setError(isHeicFile(f) ? HEIC_NOT_SUPPORTED_LINE : "Couldn't read that photo. Try a different one.");
    }
  };

  const takePhoto = () => {
    if (photoBlocked) return;
    if (kiosk) openPhotoInput(cameraRef.current, "avatar");
    else cameraRef.current?.click();
  };
  const upload = () => {
    if (photoBlocked || cameraOnly) return;
    if (kiosk) openPhotoInput(galleryRef.current, "avatar");
    else galleryRef.current?.click();
  };

  const save = () => {
    if (action === "none" || busy) return;
    setBusy(true);
    setError(null);
    const memberId = hero.member_id;
    const heroId = hero.id;
    const request =
      action === "photo" && draft.kind === "photo"
        ? () => api.uploadPhoto(memberId, heroId, draft.blob, "hero.jpg")
        : action === "remove"
          ? () => api.removePhoto(memberId, heroId)
          : draft.kind === "preset"
            ? () => api.setPreset(memberId, heroId, draft.preset)
            : null;
    if (!request) {
      setBusy(false);
      return;
    }
    const pending = (async (): Promise<string | null> => {
      try {
        const r = await request();
        if (!r.ok) return photoUploadErrorLine(r.error);
        notifyHeroRosterChanged(memberId);
        return null;
      } catch {
        return photoUploadErrorLine(null);
      }
    })();
    if (kiosk) {
      // The kiosk collapses on the tap: its upload can sit for seconds and a
      // chooser left on "Saving…" reads as broken. The request finishes after
      // the chooser is gone, so it touches no chooser state — only the result
      // goes back to the caller, which shows it on the Heroes list.
      onSubmitted(pending);
      return;
    }
    // Phone and web wait here, as before: a refusal stays on the chooser
    // with the draft intact, and success closes it.
    void pending.then((line) => {
      if (line) {
        setBusy(false);
        setError(line);
        return;
      }
      onSubmitted(Promise.resolve(null));
    });
  };

  const previewPhoto: HeroAvatarPhoto | null =
    draft.kind === "unchanged" ? current.photo : null;
  const previewPreset = draft.kind === "preset" ? draft.preset : current.preset;

  const preview = useMemo(
    () =>
      draft.kind === "photo" ? (
        <span
          className={styles.previewPhoto}
          style={{ width: previewSize, height: previewSize }}
          data-testid="chooser-preview"
          data-preview="draft-photo"
        >
          <img src={draft.url} alt={`New photo for ${name}`} width={previewSize} height={previewSize} />
        </span>
      ) : (
        <span className={styles.previewRing} data-testid="chooser-preview" data-preview={
          previewPhoto ? "photo" : previewPreset ?? "monogram"}>
          <HeroAvatar
            preset={previewPreset}
            photo={previewPhoto}
            name={name}
            size={previewSize}
            labelled
          />
        </span>
      ),
    [draft, previewPhoto, previewPreset, name, previewSize],
  );

  const photoButtons = canWrite ? (
    <div className={styles.photoBlock}>
      <div className={styles.photoButtons}>
        <button
          type="button"
          className={styles.photoButton}
          onClick={takePhoto}
          disabled={photoBlocked || busy}
          data-testid="chooser-take-photo"
        >
          <span aria-hidden="true">📷</span> Take a photo
        </button>
        {cameraOnly ? null : (
          <button
            type="button"
            className={styles.photoButton}
            onClick={upload}
            disabled={photoBlocked || busy}
            data-testid="chooser-upload"
          >
            <span aria-hidden="true">⤒</span> Upload
          </button>
        )}
      </div>
      {photoBlocked ? (
        <div className={styles.blockedLine} data-testid="chooser-photo-blocked">
          {PHOTO_BLOCKED_LINE}
        </div>
      ) : null}
      {/* CAMERA — `capture="user"` is load-bearing on the kiosk (cycle
          330.4): it is what routes the chooser to the front-lens square
          capture, MODE_AVATAR_FRONT. */}
      <input
        ref={cameraRef}
        type="file"
        accept={ACCEPT_IMAGE_CAMERA}
        capture="user"
        className={styles.hiddenInput}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          void onFile(f);
        }}
      />
      {cameraOnly ? null : (
        <input
          ref={galleryRef}
          type="file"
          accept={ACCEPT_IMAGE_GALLERY}
          className={styles.hiddenInput}
          tabIndex={-1}
          aria-hidden="true"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            void onFile(f);
          }}
        />
      )}
    </div>
  ) : null;

  const grid = canWrite ? (
    <div className={styles.gridBlock}>
      <div className={styles.orLabel}>OR PICK ONE</div>
      <div className={styles.grid} role="group" aria-label="Preset pictures">
        {HERO_AVATAR_PRESETS.map((key) => {
          const isSel = selected === key;
          return (
            <button
              key={key}
              type="button"
              className={`${styles.tile} ${isSel ? styles.tileSelected : ""}`}
              style={{ width: tileSize, height: tileSize, background: HERO_AVATAR_TINT[key] }}
              aria-pressed={isSel}
              aria-label={HERO_AVATAR_ALT[key]}
              data-testid="chooser-preset"
              data-preset={key}
              disabled={busy}
              onClick={() => setDraft({ kind: "preset", preset: key })}
            >
              <img
                src={heroAvatarSrc(key, tileSize) ?? undefined}
                alt={HERO_AVATAR_ALT[key]}
                width={tileSize}
                height={tileSize}
                draggable={false}
              />
            </button>
          );
        })}
      </div>
    </div>
  ) : null;

  const note = (
    <div className={styles.note}>
      Photos are cropped, resized and stripped of location data before they're saved.
    </div>
  );

  const removeButton =
    canWrite && hasPhoto ? (
      <button
        type="button"
        className={styles.removeButton}
        disabled={busy}
        data-testid="chooser-remove"
        onClick={() =>
          // A photo not yet saved is simply discarded; a saved one is
          // removed on Save (the hero falls back to the preset).
          setDraft(draft.kind === "photo" ? { kind: "unchanged" } : { kind: "remove" })
        }
      >
        Remove photo
      </button>
    ) : null;

  const errorLine = error ? (
    <div className={styles.error} role="alert" data-testid="chooser-error">
      {error}
    </div>
  ) : null;

  const saveButton = canWrite ? (
    <button
      type="button"
      className={styles.save}
      disabled={action === "none" || busy}
      onClick={save}
      data-testid="chooser-save"
    >
      {busy ? "Saving…" : "Save"}
    </button>
  ) : null;

  const header = (
    <div className={styles.header}>
      <button
        type="button"
        className={styles.back}
        onClick={onClose}
        aria-label="Back to Heroes"
        data-testid="chooser-back"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M12.5 4 L6.5 10 L12.5 16" fill="none" stroke="currentColor" strokeWidth="2.2"
            strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <h3 className={styles.title}>{name}'s picture</h3>
    </div>
  );

  const caption = (
    <div className={styles.caption}>This is what the kiosk shows when {name} treats</div>
  );

  const readOnlyLine = canWrite ? null : (
    <div className={styles.readOnly} data-testid="chooser-read-only">
      Only {name} or the household admin can change this picture.
    </div>
  );

  const cropper = cropSource ? (
    <AvatarCropper
      source={cropSource}
      title={`Preview · ${name}'s picture`}
      outputMaxLongEdge={512}
      onCancel={() => setCropSource(null)}
      onConfirm={(blob) => {
        setCropSource(null);
        setDraft({ kind: "photo", blob, url: URL.createObjectURL(blob) });
      }}
    />
  ) : null;

  if (kiosk) {
    return (
      <section ref={rootRef} className={`${styles.root} ${styles.kiosk}`} data-testid="hero-picture-chooser"
        data-variant="kiosk" data-read-only={canWrite ? "false" : "true"}
        data-camera-only={cameraOnly ? "true" : "false"}>
        {header}
        <div className={styles.columns}>
          <div className={styles.left}>
            {preview}
            {caption}
            {photoButtons}
            {readOnlyLine}
          </div>
          {canWrite ? (
            <div className={styles.right}>
              {grid}
              {note}
            </div>
          ) : null}
        </div>
        {canWrite ? (
          <div className={styles.footer}>
            {errorLine}
            {removeButton}
            <button type="button" className={styles.cancel} onClick={onClose} disabled={busy}
              data-testid="chooser-cancel">
              Cancel
            </button>
            {saveButton}
          </div>
        ) : null}
        {cropper}
      </section>
    );
  }

  return (
    <section ref={rootRef} className={`${styles.root} ${styles.stack} ${variant === "web" ? styles.web : styles.mobile}`}
      data-testid="hero-picture-chooser" data-variant={variant} data-read-only={canWrite ? "false" : "true"}
      data-camera-only={cameraOnly ? "true" : "false"}>
      {header}
      <div className={styles.center}>
        {preview}
        {caption}
      </div>
      {photoButtons}
      {grid}
      {canWrite ? note : null}
      {readOnlyLine}
      {removeButton}
      {errorLine}
      {saveButton}
      {cropper}
    </section>
  );
}
